import React, { useEffect, useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import {
  Accordion,
  LinkTabNavHorizontal,
  H1,
  H2,
  Heading,
  IconChevron,
  LayoutSingleColumn,
  NamedLink,
  Page,
  PaginationLinks,
  Button,
  SecondaryButton,
  Modal,
  FieldTextInput,
  Form,
  FieldRadioButton,
  IconDelete,
  InlineTextButton,
  FieldCheckbox,
} from '../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import api from '../../api';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FoorterContainer from '../../containers/FooterContainer/FooterContainer';
import { parse } from '../../util/urlHelpers';
import Spinner from '../../components/IconSpinner/IconSpinner';
import { Form as FinalForm } from 'react-final-form';
import { composeValidators, emailFormatValid, maxLength, required } from '../../util/validators';
import { useConfiguration } from '../../context/configurationContext';
import { useHistory } from 'react-router-dom';

import css from './ProductRequestsPage.module.css';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const REQUESTS_PER_PAGE = 20;
const PUBLIC_TAB = 'Public';
const PRIVATE_TAB = 'Private';
const UNRESOLVED_TAB = 'Unresolved';
const MAX_LENGTH_1000 = 1000;

const formatDate = isoString => new Date(isoString).toISOString().split('T')[0];

export const ProductRequestsPageComponent = props => {
  const {
    scrollingDisabled,
    location,
    currentUser,
    onManageDisableScrolling,
    fetchCurrentUserInProgress,
  } = props;

  const config = useConfiguration();
  const history = useHistory();
  const routes = useRouteConfiguration();

  const currentPage = parse(location.search)?.page || 1;
  const activeTab = parse(location.search)?.tab || PUBLIC_TAB;
  const isCurrentUserAdmin = currentUser?.attributes?.profile?.metadata?.isAdmin;

  const [productRequests, setProductRequests] = useState([]);
  const [pagination, setPagination] = useState({
    page: currentPage,
    totalPages: 1,
    totalItems: 0,
    perPage: REQUESTS_PER_PAGE,
  });
  const [loading, setLoading] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [productToApplyFor, setProductToApplyFor] = useState(null);
  const [applyForProductError, setApplyForProductError] = useState(null);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [requestToRemove, setRequestToRemove] = useState(null);

  const [showRemoveError, setShowRemoveError] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const [votingInProgress, setVotingInProgress] = useState(false);

  const intl = useIntl();

  const handleRejectClick = request => {
    setSelectedRequest(request);
    setRejectModalOpen(true);
  };

  const onSubmitReject = async (values, form) => {
    const data = {
      message: values?.rejectMessage,
    };

    try {
      await api.listings.rejectListingRequest(selectedRequest?._id, data);

      setProductRequests(prevRequests =>
        prevRequests.filter(request => request?._id !== selectedRequest?._id)
      );
      setRejectModalOpen(false);
      form.restart();
    } catch (error) {
      console.error('Failed to reject the request:', error);
    }
  };

  const handleAcceptClick = request => {
    setSelectedRequest(request);
    setAcceptModalOpen(true);
  };

  const onSubmitAccept = async (values, form) => {
    const data = {
      productType: values?.productType,
    };

    if (values?.acceptMessage) {
      data.message = values.acceptMessage;
    }

    try {
      await api.listings.acceptListingRequest(selectedRequest?._id, data);

      setProductRequests(prevRequests =>
        prevRequests.filter(request => request?._id !== selectedRequest?._id)
      );
      setAcceptModalOpen(false);
      form.restart();
    } catch (error) {
      console.error('Failed to accept the request:', error);
    }
  };

  const handleStatusChange = async (requestId, status) => {
    try {
      setProductRequests(prevRequests =>
        prevRequests.map(request => (request._id === requestId ? { ...request, status } : request))
      );

      await api.listings.updateRequestStatus(requestId, { status });
      setStatusError(null);
    } catch (error) {
      setStatusError(requestId);
      console.error('Failed to update the status:', error);
    }
  };

  const handleRemoveClick = request => {
    setRequestToRemove(request);
    setRemoveModalOpen(true);
  };

  const onSubmitRemove = async () => {
    try {
      await api.listings.deleteListingRequest(requestToRemove._id);

      setProductRequests(prevRequests =>
        prevRequests.filter(request => request._id !== requestToRemove._id)
      );
      setRemoveModalOpen(false);
      setShowRemoveError(false);
    } catch (error) {
      setShowRemoveError(true);
      console.error('Failed to remove the request:', error);
    }
  };

  const handleVote = async (requestId, voteType) => {
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };

      // redirect to login if the user is not logged in
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), state);
      return;
    }
    setVotingInProgress(true);
    try {
      const request = productRequests.find(req => req._id === requestId);
      const userId = currentUser?.id;

      let votePayload = { vote: 0 };

      if (voteType === 'upvote') {
        if (!request.upvotesUsers.includes(userId) && !request.downvotesUsers.includes(userId)) {
          // User hasn't voted yet, upvote
          votePayload = { vote: 1 };
        } else if (request.downvotesUsers.includes(userId)) {
          // User wants to change from downvote to upvote
          votePayload = { vote: 2 };
        } else if (request.upvotesUsers.includes(userId)) {
          // User wants to remove their upvote
          votePayload = { vote: -1 };
        }
      } else if (voteType === 'downvote') {
        if (!request.upvotesUsers.includes(userId) && !request.downvotesUsers.includes(userId)) {
          // User hasn't voted yet, downvote
          votePayload = { vote: -1 };
        } else if (request.upvotesUsers.includes(userId)) {
          // User wants to change from upvote to downvote
          votePayload = { vote: -2 };
        } else if (request.downvotesUsers.includes(userId)) {
          // User wants to remove their downvote
          votePayload = { vote: 1 };
        }
      }

      const res = await api.listings.updateRequestVotes(requestId, votePayload);

      setProductRequests(prevRequests =>
        prevRequests.map(req => (req._id === requestId ? res.data : req))
      );
    } catch (error) {
      console.error('Failed to update the vote:', error);
    } finally {
      setVotingInProgress(false);
    }
  };

  const statusOptions = [
    { value: 'New', label: 'NEW' },
    { value: 'WIP', label: 'WIP' },
    { value: 'Done', label: 'DONE' },
  ];

  const onApplyForProduct = async (values, form) => {
    const { email, message } = values;

    setApplyForProductError(null);

    try {
      await api.listings.applyForProductRequest(productToApplyFor._id, {
        email,
        message,
      });

      setProductToApplyFor(null);

      form.restart();
    } catch (error) {
      setApplyForProductError(error);
    }
  };

  const handleOpenProductApplyModal = productRequest => {
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };

      // signup and return back to listingPage.
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), state);

      return;
    }

    setProductToApplyFor(productRequest);
  };

  const tabs = [
    {
      text: (
        <Heading as="h2" rootClassName={css.tab}>
          <FormattedMessage id="ProductRequestsPage.publicTab" />
        </Heading>
      ),
      selected: activeTab === PUBLIC_TAB,
      linkProps: {
        name: 'ProductRequestsPage',
        to: { search: `?tab=${PUBLIC_TAB}` },
      },
    },
    {
      text: (
        <Heading as="h2" rootClassName={css.tab}>
          <FormattedMessage id="ProductRequestsPage.privateTab" />
        </Heading>
      ),
      selected: activeTab === PRIVATE_TAB,
      linkProps: {
        name: 'ProductRequestsPage',
        to: { search: `?tab=${PRIVATE_TAB}` },
      },
    },
    {
      text: (
        <Heading as="h2" rootClassName={css.tab}>
          <FormattedMessage id="ProductRequestsPage.unresolvedTab" />
        </Heading>
      ),
      selected: activeTab === UNRESOLVED_TAB,
      linkProps: {
        name: 'ProductRequestsPage',
        to: { search: `?tab=${UNRESOLVED_TAB}` },
      },
    },
  ];

  useEffect(() => {
    if (fetchCurrentUserInProgress) return;

    const getListingRequests = async () => {
      try {
        setLoading(true);

        const { data } = isCurrentUserAdmin
          ? await api.listings.getListingRequestsByOperator(activeTab, currentPage)
          : await api.listings.getListingRequestsByUser(currentPage);

        setProductRequests(data?.items);
        setPagination(prevState => ({
          ...prevState,
          page: data?.currentPage,
          totalPages: data?.totalPages,
          totalItems: data?.totalItems,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getListingRequests();
  }, [activeTab, currentPage, isCurrentUserAdmin, fetchCurrentUserInProgress]);

  return (
    <Page
      title={intl.formatMessage({ id: 'ProductRequestsPage.mainHeading' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FoorterContainer />}>
        <div className={css.productRequestsPage}>
          <div className={css.headings}>
            <H1>
              <FormattedMessage id="ProductRequestsPage.mainHeading" />
            </H1>
            <H2>
              <FormattedMessage id="ProductRequestsPage.subheading" />
            </H2>
            <p>
              <FormattedMessage id="ProductRequestsPage.info" />

              <NamedLink name="ProductIdeaPage" target="_blank">
                <FormattedMessage id="ProductRequestsPage.info.link" />.
              </NamedLink>
            </p>
          </div>
          {isCurrentUserAdmin && <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />}
          {loading && (
            <div className={css.spinner}>
              <Spinner />
            </div>
          )}
          {!loading && (
            <div className={css.requests}>
              {productRequests?.length === 0 && (
                <p className={css.noReqs}>
                  <FormattedMessage id="ProductRequestsPage.noRequests" />
                </p>
              )}

              {productRequests.map(request => {
                const userId = currentUser?.id?.uuid;
                const hasUpvoted = request.upvotesUsers.includes(userId);
                const hasDownvoted = request.downvotesUsers.includes(userId);

                return (
                  <div key={request?.title} className={css.productRequest}>
                    <div className={css.votes}>
                      <InlineTextButton
                        className={classNames(css.upVote, {
                          [css.voted]: hasUpvoted,
                        })}
                        onClick={() =>
                          handleVote(request._id, hasUpvoted ? 'removeVote' : 'upvote')
                        }
                        disabled={votingInProgress || hasUpvoted}
                      >
                        <IconChevron />
                      </InlineTextButton>
                      {request?.votes}
                      {/* <InlineTextButton
                        className={classNames(css.downVote, {
                          [css.voted]: hasDownvoted,
                        })}
                        onClick={() =>
                          handleVote(request._id, hasDownvoted ? 'removeVote' : 'downvote')
                        }
                        disabled={votingInProgress || hasDownvoted}
                      >
                        <IconChevron />
                      </InlineTextButton> */}
                    </div>
                    <Accordion
                      className={css.accordion}
                      title={
                        <div className={css.headerText}>
                          <div className={css.headerTitle}>{request?.title}</div>
                          <div className={css.headerInfo}>
                            <div className={css.category}>{request?.category.toUpperCase()}</div> |
                            <div>{formatDate(request?.createdAt)} | </div>
                            <div>
                              {request?.ownerType}, {request?.country}
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <div className={css.content}>
                        <div className={css.description}>
                          <b>{request?.name}</b> | {request?.description}
                        </div>

                        {isCurrentUserAdmin && request?.tab === UNRESOLVED_TAB && (
                          <div className={css.operatorManageButtons}>
                            <SecondaryButton
                              onClick={() => handleRejectClick(request)}
                              type="button"
                            >
                              <FormattedMessage id="ProductRequestsPage.rejectButton" />
                            </SecondaryButton>
                            <Button onClick={() => handleAcceptClick(request)}>
                              <FormattedMessage id="ProductRequestsPage.acceptButton" />
                            </Button>
                          </div>
                        )}
                        {!isCurrentUserAdmin && (
                          <div className={css.messageToDeveloper}>
                            <FormattedMessage id="ProductRequestsPage.messageToDeveloper" />
                            <InlineTextButton
                              type="button"
                              onClick={() => handleOpenProductApplyModal(request)}
                            >
                              <FormattedMessage id="ProductRequestsPage.messageToDeveloper.link" />
                            </InlineTextButton>
                            .
                          </div>
                        )}
                        {request?.message && (
                          <div className={css.operatorMessage}>
                            <b>
                              <FormattedMessage id="ProductRequestsPage.operator" />
                            </b>
                            {request.message}
                          </div>
                        )}
                      </div>
                    </Accordion>

                    <div className={css.controls}>
                      {isCurrentUserAdmin && request?.tab !== UNRESOLVED_TAB && (
                        <select
                          value={request?.status}
                          onChange={e => handleStatusChange(request?._id, e.target.value)}
                          className={classNames(css.status, css[request?.status.toLowerCase()])}
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}

                      {!isCurrentUserAdmin && (
                        <div className={classNames(css.status, css[request?.status.toLowerCase()])}>
                          {request?.status.toUpperCase()}
                        </div>
                      )}

                      {isCurrentUserAdmin && request?.tab === UNRESOLVED_TAB && (
                        <div className={classNames(css.status, css.productTypeLabel)}>
                          {request?.productType.toUpperCase()}
                        </div>
                      )}

                      {isCurrentUserAdmin && statusError === request?._id && (
                        <div className={css.statusErrorText}>
                          <FormattedMessage id="ProductRequestsPage.statusError" />
                        </div>
                      )}

                      {isCurrentUserAdmin && (
                        <InlineTextButton
                          rootClassName={css.removeButton}
                          onClick={() => handleRemoveClick(request)}
                          type="button"
                          title={intl.formatMessage({ id: 'ProductRequestsPage.removeButton' })}
                        >
                          <IconDelete />
                        </InlineTextButton>
                      )}
                    </div>
                  </div>
                );
              })}

              <NamedLink className={css.submitButton} name="ProductIdeaPage" target="_blank">
                <FormattedMessage id="ProductRequestsPage.submitButton" />
              </NamedLink>

              {pagination?.totalPages > 1 && (
                <PaginationLinks
                  className={css.pagination}
                  pageName="ProductRequestsPage"
                  pagination={pagination}
                  pageSearchParams={{ tab: activeTab }}
                />
              )}
            </div>
          )}
        </div>
      </LayoutSingleColumn>

      <Modal
        id="rejectModal"
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <FinalForm
          onSubmit={onSubmitReject}
          render={fieldRenderProps => {
            const { handleSubmit, formId, submitting, pristine, form } = fieldRenderProps;

            // reject message
            const rejectMessageRequiredMessage = intl.formatMessage({
              id: 'ProductRequestsPage.rejectMessageRequired',
            });
            const rejectMessageMaxLength = maxLength(
              intl.formatMessage(
                { id: 'ProductRequestsPage.maxLength' },
                {
                  maxLength: MAX_LENGTH_1000,
                }
              ),
              MAX_LENGTH_1000
            );

            return (
              <Form onSubmit={handleSubmit} className={css.modalForm}>
                <p>
                  <FormattedMessage id="ProductRequestsPage.rejectTitle" />
                </p>

                <FieldTextInput
                  className={css.rejectMessage}
                  id={formId ? `${formId}rejectMessage` : 'rejectMessage'}
                  name="rejectMessage"
                  type="textarea"
                  label={intl.formatMessage({ id: 'ProductRequestsPage.rejectMessage' })}
                  placeholder={intl.formatMessage({
                    id: 'ProductRequestsPage.rejectMessagePlaceholder',
                  })}
                  maxLength={MAX_LENGTH_1000}
                  validate={composeValidators(
                    required(rejectMessageRequiredMessage),
                    rejectMessageMaxLength
                  )}
                />
                <div className={css.modalButtons}>
                  <SecondaryButton
                    onClick={() => {
                      setRejectModalOpen(false);
                      form.restart();
                    }}
                    type="button"
                  >
                    <FormattedMessage id="ProductRequestsPage.cancelButton" />
                  </SecondaryButton>
                  <Button type="submit" disabled={pristine || submitting}>
                    <FormattedMessage id="ProductRequestsPage.continueButton" />
                  </Button>
                </div>
              </Form>
            );
          }}
        />
      </Modal>

      <Modal
        id="acceptModal"
        isOpen={acceptModalOpen}
        onClose={() => setAcceptModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <FinalForm
          initialValues={{
            productType: selectedRequest?.productType,
          }}
          onSubmit={onSubmitAccept}
          render={fieldRenderProps => {
            const { handleSubmit, formId, form } = fieldRenderProps;

            // accept message
            const acceptMessageMaxLength = maxLength(
              intl.formatMessage(
                { id: 'ProductRequestsPage.maxLength' },
                {
                  maxLength: MAX_LENGTH_1000,
                }
              ),
              MAX_LENGTH_1000
            );

            return (
              <Form onSubmit={handleSubmit} className={css.modalForm}>
                <p>
                  <FormattedMessage id="ProductRequestsPage.acceptTitle" />
                </p>

                <div className={css.productTypeButtons}>
                  <FieldRadioButton
                    className={css.productTypeButton}
                    id="generic"
                    name="productType"
                    label={intl.formatMessage({
                      id: 'ProductRequestsPage.genericType',
                    })}
                    value="Generic"
                  />
                  <FieldRadioButton
                    className={css.productTypeButton}
                    id="custom"
                    name="productType"
                    label={intl.formatMessage({ id: 'ProductRequestsPage.customType' })}
                    value="Custom"
                  />
                </div>

                <FieldTextInput
                  className={css.acceptMessage}
                  id={formId ? `${formId}acceptMessage` : 'acceptMessage'}
                  name="acceptMessage"
                  type="textarea"
                  label={intl.formatMessage({ id: 'ProductRequestsPage.acceptMessage' })}
                  maxLength={MAX_LENGTH_1000}
                  validate={composeValidators(acceptMessageMaxLength)}
                />
                <div className={css.modalButtons}>
                  <SecondaryButton
                    onClick={() => {
                      setAcceptModalOpen(false);
                      form.restart();
                    }}
                    type="button"
                  >
                    <FormattedMessage id="ProductRequestsPage.cancelButton" />
                  </SecondaryButton>
                  <Button type="submit">
                    <FormattedMessage id="ProductRequestsPage.continueButton" />
                  </Button>
                </div>
              </Form>
            );
          }}
        />
      </Modal>

      <Modal
        id="removeModal"
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <FinalForm
          onSubmit={onSubmitRemove}
          render={({ handleSubmit }) => (
            <Form onSubmit={handleSubmit} className={css.modalForm}>
              <p>
                <FormattedMessage id="ProductRequestsPage.removeTitle" />
              </p>

              <FormattedMessage
                id="ProductRequestsPage.removeContent"
                values={{ name: <b>{requestToRemove?.title}</b> }}
              />

              {showRemoveError && (
                <div className={css.removeErrorText}>
                  <FormattedMessage id="ProductRequestsPage.removeError" />
                </div>
              )}

              <div className={css.modalButtons}>
                <SecondaryButton onClick={() => setRemoveModalOpen(false)} type="button">
                  <FormattedMessage id="ProductRequestsPage.cancelButton" />
                </SecondaryButton>
                <Button type="submit">
                  <FormattedMessage id="ProductRequestsPage.continueButton" />
                </Button>
              </div>
            </Form>
          )}
        />
      </Modal>

      <Modal
        id="applyForProductModal"
        isOpen={!!productToApplyFor}
        onClose={() => setProductToApplyFor(null)}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <FinalForm
          initialValues={{
            email: currentUser?.attributes?.email,
          }}
          onSubmit={onApplyForProduct}
          render={formRenderProps => {
            const { handleSubmit, form, values } = formRenderProps;

            const messageMaxLength = maxLength(
              intl.formatMessage(
                { id: 'ProductRequestsPage.maxLength' },
                {
                  maxLength: MAX_LENGTH_1000,
                }
              ),
              MAX_LENGTH_1000
            );

            const emailRequiredMessage = intl.formatMessage({
              id: 'LoginForm.emailRequired',
            });
            const emailRequired = required(emailRequiredMessage);
            const emailInvalidMessage = intl.formatMessage({
              id: 'LoginForm.emailInvalid',
            });
            const emailValid = emailFormatValid(emailInvalidMessage);

            const termsLink = (
              <NamedLink name="TermsOfServicePage" target="_blank">
                <FormattedMessage id="AuthenticationPage.termsAndConditionsTermsLinkText" />
              </NamedLink>
            );

            const privacyLink = (
              <NamedLink name="PrivacyPolicyPage" target="_blank">
                <FormattedMessage id="AuthenticationPage.termsAndConditionsPrivacyLinkText" />
              </NamedLink>
            );

            const submitDisabled = !values.terms?.length;

            return (
              <Form onSubmit={handleSubmit}>
                <div className={css.modalTitle}>
                  <FormattedMessage id="ProductRequestsPage.applyForProductTitle" />

                  <div>{productToApplyFor?.title}</div>
                </div>

                <p className={css.modalDesc}>
                  <FormattedMessage id="ProductRequestsPage.description" />
                </p>

                <FieldTextInput
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage(
                    {
                      id: 'ProductRequestsPage.applyForProductEmailLabel',
                    },
                    {
                      marketplaceName: config.marketplaceName,
                    }
                  )}
                  placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                  validate={composeValidators(emailRequired, emailValid)}
                />

                <FieldTextInput
                  className={css.acceptMessage}
                  id="message"
                  name="message"
                  type="textarea"
                  label={intl.formatMessage({ id: 'ProductRequestsPage.messageToOperator' })}
                  placeholder={intl.formatMessage(
                    {
                      id: 'ProductRequestsPage.messageToOperatorPlaceholder',
                    },
                    { productName: productToApplyFor?.title }
                  )}
                  maxLength={MAX_LENGTH_1000}
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'General.fieldRequired' })),
                    messageMaxLength
                  )}
                />

                <FieldCheckbox
                  name="terms"
                  id="terms-accepted"
                  value="true"
                  label={intl.formatMessage(
                    { id: 'AuthenticationPage.termsAndConditionsAcceptText' },
                    { termsLink, privacyLink }
                  )}
                  className={css.terms}
                />

                {applyForProductError ? (
                  <p className={css.error}>{intl.formatMessage({ id: 'Topbar.genericError' })}</p>
                ) : null}

                <div className={css.modalButtons}>
                  <SecondaryButton
                    onClick={() => {
                      setProductToApplyFor(null);
                      form.restart();
                    }}
                    type="button"
                  >
                    <FormattedMessage id="ProductRequestsPage.cancelButton" />
                  </SecondaryButton>
                  <Button type="submit" disabled={submitDisabled}>
                    <FormattedMessage id="General.send" />
                  </Button>
                </div>
                <div className={css.operatorNote}>
                  <FormattedMessage id="ProductRequestsPage.operatorReviewNote" />
                </div>
              </Form>
            );
          }}
        />
      </Modal>
    </Page>
  );
};

ProductRequestsPageComponent.propTypes = {
  scrollingDisabled: bool,
  location: shape({
    search: string,
  }),
  currentUser: propTypes.currentUser,
  onManageDisableScrolling: func,
};

const mapStateToProps = state => {
  const { currentUser, fetchCurrentUserInProgress } = state?.user || {};
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    fetchCurrentUserInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProductRequestsPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProductRequestsPageComponent);

export default ProductRequestsPage;
